import React, { createContext, useContext, useState, useEffect } from 'react'
import { api } from '../services/api'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [permissions, setPermissions] = useState({})

  useEffect(() => {
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/auth/check.php')
      // check.php retorna { ok, authenticated, user }
      if (response.ok && response.authenticated) {
        setIsAuthenticated(true)
        setUser(response.user || null)
        if (response.user?.id_funcionario) {
          await loadPermissions(response.user.id_funcionario, response.user.id_empresa)
        }
      } else {
        setIsAuthenticated(false)
        setUser(null)
        setPermissions({})
      }
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error)
      setIsAuthenticated(false)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials) => {
    try {
      const response = await api.post('/auth/login.php', credentials)
      
      if (response.ok) {
        setIsAuthenticated(true)
        setUser(response.user || null)
        if (response.user?.id_funcionario) {
          await loadPermissions(response.user.id_funcionario, response.user.id_empresa)
        }
        return { success: true, user: response.user || null }
      } else {
        return {
          success: false,
          error: response.message || 'Erro no login',
          tentativasUsadas: response.tentativas_usadas,
          tentativasMax: response.tentativas_max,
          tentativasRestantes: response.tentativas_restantes,
          bloqueado: !!response.bloqueado,
          bloqueioRestanteMinutos: response.bloqueio_restante_minutos
        }
      }
    } catch (error) {
      console.error('💥 Erro no login:', error)
      return { success: false, error: error.message || 'Erro de conexão' }
    }
  }

  const loadPermissions = async (idFuncionario, idEmpresa) => {
    try {
      const res = await api.get('/permissoes/get.php', { id_funcionario: idFuncionario })
      if (res.ok) {
        setPermissions(res.data || {})
      } else {
        setPermissions({})
      }
    } catch (err) {
      setPermissions({})
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout.php')
    } catch (error) {
      console.error('Erro no logout:', error)
    } finally {
      setIsAuthenticated(false)
      setUser(null)
    }
  }

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    checkAuthStatus,
    permissions,
    loadPermissions
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
